import React from 'react';
import { useData } from '../../context/DataContext';
import { computeGroupStats } from '../../utils/calculations';

export const SchoolSnapshotKPIs = ({ onDrilldown }) => {
  const { getScopedStudents } = useData();

  const scoped = getScopedStudents();
  const stats = computeGroupStats(scoped);

  const pct = (v) => {
    return stats.participated > 0 ? Math.round((v / stats.participated) * 100) + '%' : '0%';
  };

  return (
    <div className="mb-8 select-none">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between mb-1 gap-3">
        <h3 className="text-2xl font-bold text-gray-800">School Snapshot</h3>
        <div className="flex flex-wrap gap-5 text-xs font-semibold">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#2a6a4a' }}></span>
            <span className="text-gray-700"><b style={{ color: '#2a6a4a' }}>Competent</b> passed all levels</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#d4870a' }}></span>
            <span className="text-gray-700"><b style={{ color: '#d4870a' }}>Bridge the Gap</b> 1-2 yrs below</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#dc3545' }}></span>
            <span className="text-gray-700"><b style={{ color: '#dc3545' }}>Remediation Need</b> 3+ yrs below</span>
          </span>
        </div>
      </div>
      <p className="text-xs italic text-gray-400 mb-4">
        *A single weak level (60-79%) is forgiven if every other level is ≥80% — that subject still counts as passed.
      </p>

      {/* 5 KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {/* Students Assessed */}
        <div
          className="bg-white rounded-2xl p-5 shadow border-l-4 cursor-pointer hover:shadow-lg transition-shadow"
          style={{ borderColor: '#4f46e5' }}
          onClick={() => onDrilldown('all')}
          title="Click to view students"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">👥</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#4f46e5' }}>
              Students Assessed
            </span>
          </div>
          <p className="text-4xl font-black text-gray-900">{stats.participated}</p>
          <p className="text-xs text-gray-500 mt-1">of {stats.total} enrolled</p>
        </div>

        {/* Overall Avg Score */}
        <div
          className="bg-white rounded-2xl p-5 shadow border-l-4 cursor-pointer hover:shadow-lg transition-shadow"
          style={{ borderColor: '#1e3a5f' }}
          onClick={() => onDrilldown('all')}
          title="Click to view students"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">🎯</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#1e3a5f' }}>
              Overall Avg Score
            </span>
          </div>
          <p className="text-4xl font-black" style={{ color: '#1e3a5f' }}>
            {stats.participated > 0 ? Math.round(stats.avgOverall) + '%' : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Combined English &amp; Math</p>
        </div>

        {/* At Grade Level */}
        <div
          className="bg-white rounded-2xl p-5 shadow border-l-4 cursor-pointer hover:shadow-lg transition-shadow"
          style={{ borderColor: '#2a6a4a' }}
          onClick={() => onDrilldown('competent')}
          title="Click to view students"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">☀️</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#2a6a4a' }}>
              At Grade Level
            </span>
          </div>
          <p className="text-4xl font-black" style={{ color: '#2a6a4a' }}>
            {pct(stats.comp)}
          </p>
          <p className="text-xs text-gray-500 mt-1">{stats.comp} students competent</p>
        </div>

        {/* Bridge the Gap */}
        <div
          className="bg-white rounded-2xl p-5 shadow border-l-4 cursor-pointer hover:shadow-lg transition-shadow"
          style={{ borderColor: '#d4870a' }}
          onClick={() => onDrilldown('bridge')}
          title="Click to view students"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">📈</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#d4870a' }}>
              Bridge the Gap
            </span>
          </div>
          <p className="text-4xl font-black" style={{ color: '#d4870a' }}>
            {pct(stats.bridge)}
          </p>
          <p className="text-xs text-gray-500 mt-1">{stats.bridge} need targeted support</p>
        </div>

        {/* Remediation Need */}
        <div
          className="bg-white rounded-2xl p-5 shadow border-l-4 cursor-pointer hover:shadow-lg transition-shadow"
          style={{ borderColor: '#dc3545' }}
          onClick={() => onDrilldown('remediation')}
          title="Click to view students"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">⚠️</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#dc3545' }}>
              Remediation Need
            </span>
          </div>
          <p className="text-4xl font-black" style={{ color: '#dc3545' }}>
            {pct(stats.remed)}
          </p>
          <p className="text-xs text-gray-500 mt-1">{stats.remed} need foundational program</p>
        </div>
      </div>
    </div>
  );
};
export default SchoolSnapshotKPIs;
